import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";

import {
  COLOR_TOKEN_COPY,
  COLOR_TOKENS,
  OPEN_FILL_MODE_COPY,
  OPEN_FILL_MODE_HELP,
} from "../../../../packages/shared/src/schedule-color.ts";
import {
  DEFAULT_HELP_ARTICLE_ID,
  HELP_INDEX,
  findHelpEntry,
  helpPlannedCount,
  helpPublishedCount,
  type HelpIndexEntry,
} from "../help/help-articles";
import type { ShellOutletContext } from "../nav/AppShell";
import { DocumentLayout } from "../components/shell/DocumentLayout";
import { ShellSubrail, type ShellSubrailItem } from "../components/shell/ShellSubrail";
import { ProductFeedbackSheet } from "../components/ProductFeedbackSheet";
import type { ProductFeedbackType } from "../data/product-feedback";

type FeedbackLaunch = (type: ProductFeedbackType, trigger: HTMLButtonElement) => void;
const FeedbackLaunchContext = createContext<FeedbackLaunch>(() => undefined);

/**
 * Superuser-only Help shell (console design v1; shell v3 document template 2026-08-15).
 *
 * - Nav + page gated on platform superadmin (`isSuperadmin`), same posture as Platform.
 * - Static index: published React articles + honest "planned" stubs. No CMS, no schema.
 * - Every published article closes with the two human-only feedback capture doors.
 * - The index is the shell subrail (grouped by section, planned entries inert), the
 *   article soft-caps at 76ch with balanced margins, and the utility rail carries the
 *   in-section jumps so ultrawide reads balanced instead of stranding a void (board
 *   frame 13).
 */
export function HelpPage() {
  const { client, config, isSuperadmin } = useOutletContext<ShellOutletContext>();
  // Deep link (?article=id) so founder surfaces like the Platform access queue can land on a
  // specific runbook. Initial-state only; in-page navigation stays plain component state.
  const [searchParams] = useSearchParams();
  const requestedArticle = searchParams.get("article");
  const [activeId, setActiveId] = useState(
    requestedArticle !== null && findHelpEntry(requestedArticle) !== undefined
      ? requestedArticle
      : DEFAULT_HELP_ARTICLE_ID,
  );

  const subrailItems = useMemo<ShellSubrailItem[]>(
    () =>
      HELP_INDEX.map((entry) => ({
        key: entry.id,
        label: entry.title,
        group: entry.section,
        ...(entry.status === "planned"
          ? { disabled: true, tag: "planned", hint: entry.plannedReason ?? "Not written yet" }
          : {}),
      })),
    [],
  );
  const published = helpPublishedCount(HELP_INDEX);
  const planned = helpPlannedCount(HELP_INDEX);
  const active = findHelpEntry(activeId) ?? findHelpEntry(DEFAULT_HELP_ARTICLE_ID)!;
  const [feedbackType, setFeedbackType] = useState<ProductFeedbackType | null>(null);
  const feedbackTriggerRef = useRef<HTMLButtonElement | null>(null);
  const openFeedback = useCallback<FeedbackLaunch>((type, trigger) => {
    feedbackTriggerRef.current = trigger;
    setFeedbackType(type);
  }, []);
  const closeFeedback = useCallback(() => {
    setFeedbackType(null);
    window.requestAnimationFrame(() => feedbackTriggerRef.current?.focus());
  }, []);

  if (!isSuperadmin) {
    return (
      <DocumentLayout>
        <h1 className="page__title">Help</h1>
        <p className="page__note">
          This help preview is only available to superusers while we fill it in.
        </p>
      </DocumentLayout>
    );
  }

  return (
    <FeedbackLaunchContext.Provider value={openFeedback}>
      <DocumentLayout
        toc={
          <ShellSubrail
            eyebrow="Help"
            items={subrailItems}
            activeKey={active.id}
            onSelect={setActiveId}
          />
        }
        aside={<HelpSectionRail active={active} onSelect={setActiveId} />}
      >
        <div className="help-su-banner" role="status">
          <span className="help-su-banner__icon" aria-hidden="true">
            ◐
          </span>
          <span>
            <strong>Superuser preview.</strong>{" "}
            <span className="help-su-banner__muted">
              Hidden from non-superusers while we fill this in.
            </span>
          </span>
          <span className="help-su-banner__count">
            {published} published · {planned} planned
          </span>
        </div>

        {renderPublishedArticle(active, setActiveId) ?? (
          <PlannedArticlePlaceholder entry={active} />
        )}
      </DocumentLayout>
      {feedbackType !== null ? (
        <ProductFeedbackSheet
          client={client}
          accountId={config.accountId}
          accountName={config.accountName}
          roleLabel="Platform superadmin"
          pageLabel={`Help · ${active.title}`}
          initialType={feedbackType}
          onClose={closeFeedback}
        />
      ) : null}
    </FeedbackLaunchContext.Provider>
  );
}

/** The utility rail (board frame 13): in-section jumps so the right side of an ultrawide
 *  earns its width instead of stranding black. Below 1600px it follows the article. */
function HelpSectionRail({
  active,
  onSelect,
}: {
  active: HelpIndexEntry;
  onSelect: (id: string) => void;
}) {
  const entries = HELP_INDEX.filter((entry) => entry.section === active.section);
  return (
    <nav className="help-aside" aria-label="In this section">
      <p className="help-aside__label">In this section</p>
      <ul className="help-aside__list">
        {entries.map((entry) =>
          entry.status === "published" ? (
            <li key={entry.id}>
              <button
                type="button"
                className={
                  entry.id === active.id
                    ? "help-aside__link help-aside__link--active"
                    : "help-aside__link"
                }
                aria-current={entry.id === active.id ? "true" : undefined}
                onClick={() => onSelect(entry.id)}
              >
                {entry.title}
              </button>
            </li>
          ) : (
            <li key={entry.id} className="help-aside__planned">
              {entry.title} <span className="help-planned-tag">planned</span>
            </li>
          ),
        )}
      </ul>
    </nav>
  );
}

/** Exported for the wiring test: every published index id must resolve to a real body. */
export function renderPublishedArticle(
  entry: HelpIndexEntry,
  onSelect: (id: string) => void,
): ReactNode {
  if (entry.status !== "published") return null;
  switch (entry.id) {
    case "console-basics":
      return <ConsoleBasicsArticle onSelect={onSelect} />;
    case "drafts-then-publish":
      return <DraftsThenPublishArticle onSelect={onSelect} />;
    case "open-shifts-claiming":
      return <OpenShiftsAndClaimingArticle onSelect={onSelect} />;
    case "template-colors-labels":
      return <TemplateColorsAreLabelsArticle onSelect={onSelect} />;
    case "alert-channel-policies":
      return <AlertChannelPoliciesArticle onSelect={onSelect} />;
    case "inviting-team":
      return <InvitingYourTeamArticle onSelect={onSelect} />;
    case "routes-checkpoints":
      return <RoutesAndCheckpointsArticle onSelect={onSelect} />;
    case "roles-permissions":
      return <RolesAndPermissionsArticle onSelect={onSelect} />;
    case "sos-to-incident":
      return <SosToIncidentArticle onSelect={onSelect} />;
    case "guard-app-access-queue":
      return <GuardAppAccessQueueArticle onSelect={onSelect} />;
    case "phone-staging-beta":
      return <PhoneStagingBetaArticle onSelect={onSelect} />;
    default:
      return null;
  }
}

function HelpArticleFrame({
  section,
  title,
  meta,
  children,
  related,
}: {
  section: string;
  title: string;
  meta: string;
  children: ReactNode;
  related?: ReactNode;
}) {
  return (
    // A div, not an article: DocumentLayout's .docl__article already provides the article
    // landmark, and nesting a second one would double the role.
    <div className="help-article">
      <div className="help-crumbs">
        Help · <b>{section}</b>
      </div>
      <h1 className="help-article__title">{title}</h1>
      <p className="help-article__meta">{meta}</p>
      {children}
      {related}
      <ShareIdeaFooter />
    </div>
  );
}

function HelpRelated({
  items,
  onSelect,
}: {
  items: { id: string; title: string }[];
  onSelect: (id: string) => void;
}) {
  return (
    <div className="help-related">
      <span className="help-related__label">Related</span>
      <ul>
        {items.map((item) => {
          const entry = findHelpEntry(item.id);
          if (entry?.status === "published") {
            return (
              <li key={item.id}>
                <button
                  type="button"
                  className="help-related__link"
                  onClick={() => onSelect(item.id)}
                >
                  {item.title}
                </button>
              </li>
            );
          }
          return (
            <li key={item.id}>
              {item.title} <span className="help-planned-tag">planned</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function PlannedArticlePlaceholder({ entry }: { entry: HelpIndexEntry }) {
  return (
    <HelpArticleFrame
      section={entry.section}
      title={entry.title}
      meta="Planned · not written yet"
    >
      <p className="help-article__body">
        This article is on the roadmap. Superusers can see the index while we write real content;
        nothing here pretends to exist.
      </p>
      {entry.plannedReason !== undefined ? (
        <p className="help-article__body">
          <strong>Why not yet:</strong> {entry.plannedReason}
        </p>
      ) : null}
    </HelpArticleFrame>
  );
}

/** Dispatch orientation: who Console is for, main nav labels, Help gate, icon rail. */
function ConsoleBasicsArticle({ onSelect }: { onSelect: (id: string) => void }) {
  return (
    <HelpArticleFrame
      section="Getting around"
      title="Console basics"
      meta="Updated Aug 11, 2026 · applies to Console Dispatch shell"
      related={
        <HelpRelated
          onSelect={onSelect}
          items={[
            { id: "roles-permissions", title: "Roles & permissions" },
            { id: "drafts-then-publish", title: "Drafts then Publish in Builder" },
            { id: "open-shifts-claiming", title: "Open shifts & claiming" },
            { id: "alert-channel-policies", title: "Alert channel policies" },
          ]}
        />
      }
    >
      <p className="help-article__body">
        The <strong>SecOps Console</strong> is the Dispatch product for supervisors and account admins.
        You run live operations, schedule, setup, company communications, and account settings from
        a browser. Field officers use the mobile app and guard web for their own shifts and claims.
        This Help does not invent those field flows.
      </p>

      <h2 className="help-article__h2">Primary navigation</h2>
      <p className="help-article__body">
        The left rail is labeled <strong>Dispatch</strong>. Items you may see (permission and role
        gates can hide some of them):
      </p>
      <ul className="help-article__list">
        <li>
          <strong>Inbox</strong> · Critical alerts, Action follow-up, announcement Boards, and
          Direct or Crew Messages. Critical opens the existing Dispatch response tools.
        </li>
        <li>
          <strong>Command Center</strong> - live CAD map and response tools for the workspace.
          Alerts remain authoritative here even when Inbox links to them.
        </li>
        <li>
          <strong>Patrols</strong> - routes, checkpoints, and related patrol work.
        </li>
        <li>
          <strong>Schedule</strong> - coverage and Schedule Builder drafts (publish before officers
          see changes).
        </li>
        <li>
          <strong>Setup</strong> - rollout and property setup (needs route rollout manage).
        </li>
        <li>
          <strong>Incidents</strong> - incident records and review.
        </li>
        <li>
          <strong>Reports</strong> - reporting surfaces for the account.
        </li>
        <li>
          <strong>Admin</strong> - members, devices, policies, alerts matrix, and other account
          management (needs member manage for the nav entry).
        </li>
        <li>
          <strong>Platform</strong> - platform superadmin only: the Guard app
          access queue, account controls, and cross-tenant devices and sessions, in tabs.
        </li>
        <li>
          <strong>Help</strong> - this knowledge shell (platform superadmin only, same gate as
          Platform).
        </li>
      </ul>

      <div className="help-callout" role="note">
        <p>
          <strong>Help is a superuser preview.</strong> Non-superusers do not see the Help nav item.
          The blue banner on this page means the same thing: hidden from non-superusers while we
          fill articles in. Sidebar entries tagged <strong>planned</strong> are honest stubs, not
          live docs.
        </p>
      </div>

      <h2 className="help-article__h2">Icon rail and workspace</h2>
      <ul className="help-article__list">
        <li>
          On a wide desktop layout you can <strong>Collapse</strong> the sidenav to an icon rail.
          Each item still has its real label (tooltip and accessible name match the full label).
          On a narrow phone drawer the rail stays full-width with labels.
        </li>
        <li>
          The top bar shows the current <strong>Workspace</strong> account. If you belong to more
          than one account, the workspace picker switches which tenant the desk is reading.
        </li>
        <li>
          <strong>Jump</strong> in the top bar (or Cmd+K / Ctrl+K) opens a find-and-go palette:
          pages you are allowed to see, plus officer and site search. It never shows a page your
          permissions hide.
        </li>
        <li>
          Theme toggle and sign-out sit in the top bar. Runtime version appears at the bottom of the
          sidenav as a compact build stamp.
        </li>
      </ul>

      <h2 className="help-article__h2">How to use Help</h2>
      <p className="help-article__body">
        Open published articles from the left index. Related links at the bottom of an article jump
        to other published topics. Planned titles stay disabled until written. At the bottom of
        each article, Report a problem and Share an idea open a private note to the SecOps team.
      </p>

      <div
        className="help-flow"
        role="img"
        aria-label="Flow: icon rail, main areas, Help for superusers"
      >
        <span className="help-flow__step">
          <span className="help-flow__name">Icon rail</span>
          <span className="help-flow__sub">Command Center … Help</span>
        </span>
        <span className="help-flow__arrow" aria-hidden="true">
          →
        </span>
        <span className="help-flow__step">
          <span className="help-flow__name">Main area</span>
          <span className="help-flow__sub">page for that section</span>
        </span>
        <span className="help-flow__arrow" aria-hidden="true">
          →
        </span>
        <span className="help-flow__step help-flow__step--live">
          <span className="help-flow__name">Help</span>
          <span className="help-flow__sub">superuser preview only</span>
        </span>
      </div>
      <p className="help-flow__cap">
        Use the rail labels above as the product vocabulary. Do not invent mobile or guard-web
        screens here.
      </p>
    </HelpArticleFrame>
  );
}

function DraftsThenPublishArticle({ onSelect }: { onSelect: (id: string) => void }) {
  return (
    <HelpArticleFrame
      section="Schedule"
      title="Drafts then Publish in Builder"
      meta="Updated Aug 8, 2026 · applies to Console · Schedule Builder"
      related={
        <HelpRelated
          onSelect={onSelect}
          items={[
            { id: "open-shifts-claiming", title: "Open shifts & claiming" },
            { id: "template-colors-labels", title: "Template colors are labels" },
          ]}
        />
      }
    >
      <p className="help-article__body">
        The Schedule Builder always edits a <strong>draft</strong>. Nothing you change - shifts,
        templates, assignments - is visible to officers until you publish. That means you can rework
        a whole week safely, in as many sittings as you need.
      </p>

      <h2 className="help-article__h2">Work in the draft</h2>
      <ul className="help-article__list">
        <li>
          Every edit saves to the draft automatically. There is no separate &quot;save&quot; step to
          forget.
        </li>
        <li>
          The Builder header shows <strong>Draft - not visible to officers</strong> the whole time
          you&apos;re editing.
        </li>
        <li>
          Template colors are <strong>labels for you</strong> - they group shifts visually and never
          mean status. A template colored green is not &quot;verified&quot;; verified states keep
          their own proof styling.
        </li>
      </ul>

      <div className="help-callout" role="note">
        <p>Draft edits never notify officers. Notifications go out once - when you publish.</p>
      </div>

      <h2 className="help-article__h2">Publish when you&apos;re ready</h2>
      <p className="help-article__body">
        Publishing starts with a <strong>review summary</strong>: what the draft adds, changes, and
        removes compared to what officers currently see. Nothing goes live until you confirm it.
      </p>
      <ul className="help-article__list">
        <li>Confirm the summary and the schedule updates for every affected officer.</li>
        <li>
          Officers with app notifications enabled are told their schedule changed - once, at publish.
        </li>
        <li>After publishing, the Builder returns to a fresh draft on top of the live schedule.</li>
      </ul>

      <div className="help-flow" role="img" aria-label="Flow: draft, review changes, published">
        <span className="help-flow__step">
          <span className="help-flow__name">Draft</span>
          <span className="help-flow__sub">only supervisors see it</span>
        </span>
        <span className="help-flow__arrow" aria-hidden="true">
          →
        </span>
        <span className="help-flow__step">
          <span className="help-flow__name">Review changes</span>
          <span className="help-flow__sub">added · changed · removed</span>
        </span>
        <span className="help-flow__arrow" aria-hidden="true">
          →
        </span>
        <span className="help-flow__step help-flow__step--live">
          <span className="help-flow__name">Published</span>
          <span className="help-flow__sub">live to officers</span>
        </span>
      </div>
      <p className="help-flow__cap">Publishing is the only step that changes what officers see.</p>
    </HelpArticleFrame>
  );
}

/** Open seats: instant claim vs request approval (template open-fill policy). */
function OpenShiftsAndClaimingArticle({ onSelect }: { onSelect: (id: string) => void }) {
  return (
    <HelpArticleFrame
      section="Schedule"
      title="Open shifts & claiming"
      meta="Updated Aug 8, 2026 · applies to Console and officer open seats · Wave 6"
      related={
        <HelpRelated
          onSelect={onSelect}
          items={[
            { id: "drafts-then-publish", title: "Drafts then Publish in Builder" },
            { id: "template-colors-labels", title: "Template colors are labels" },
          ]}
        />
      }
    >
      <p className="help-article__body">
        An <strong>open seat</strong> is a published shift that still needs someone. Officers see
        those seats after you publish (drafts never put open work in front of the field). How they
        take a seat depends on the template&apos;s <strong>open-fill</strong> setting, not on the
        template color chip.
      </p>

      <h2 className="help-article__h2">Two fill paths</h2>
      <p className="help-article__body">
        Each shift template picks one open-fill mode. The product language matches what supervisors
        set in the template editor:
      </p>
      <ul className="help-article__list">
        <li>
          <strong>{OPEN_FILL_MODE_COPY.instant_claim}.</strong> {OPEN_FILL_MODE_HELP.instant_claim}{" "}
          The seat fills as soon as an eligible officer claims it, up to the shift headcount.
        </li>
        <li>
          <strong>{OPEN_FILL_MODE_COPY.request_approval}.</strong>{" "}
          {OPEN_FILL_MODE_HELP.request_approval} The seat stays open until a desk supervisor
          approves a request from the open-shift request inbox.
        </li>
      </ul>
      <p className="help-article__body">
        Instant claim and request approval can both appear on the same account across different
        templates. Color does not choose the path: a green Night template and a blue Overtime
        template can use opposite fill modes.
      </p>

      <div className="help-callout" role="note">
        <p>
          Template color is a <strong>label for the desk</strong>, not duty status and not proof.
          Officers must not read a template color as SOS, OK, or verified. Status and safety keep
          their own styling. See Template colors are labels for the full rule.
        </p>
      </div>

      <h2 className="help-article__h2">What the desk does</h2>
      <ul className="help-article__list">
        <li>
          Publish the schedule so open seats appear for eligible officers (draft edits stay
          invisible until publish).
        </li>
        <li>
          For request-approval templates, work the pending list: approve to fill the seat, or
          decline when the request should not land.
        </li>
        <li>
          When you decline, include a short reason so the officer knows what happened. Officer apps
          show that reason when the decline-reason surface is live for their build.
        </li>
      </ul>

      <div className="help-flow" role="img" aria-label="Flow: open seat, claim or request, filled or declined">
        <span className="help-flow__step">
          <span className="help-flow__name">Open seat</span>
          <span className="help-flow__sub">published · still needs fill</span>
        </span>
        <span className="help-flow__arrow" aria-hidden="true">
          →
        </span>
        <span className="help-flow__step">
          <span className="help-flow__name">Claim or request</span>
          <span className="help-flow__sub">per template open-fill</span>
        </span>
        <span className="help-flow__arrow" aria-hidden="true">
          →
        </span>
        <span className="help-flow__step help-flow__step--live">
          <span className="help-flow__name">Filled</span>
          <span className="help-flow__sub">or declined with reason</span>
        </span>
      </div>
      <p className="help-flow__cap">
        Request-approval seats need a supervisor decision. Instant claim fills at the officer&apos;s
        claim when a seat remains.
      </p>
    </HelpArticleFrame>
  );
}

/** ADR-007 in operator language: closed palette is labels; proof green stays proof. */
function TemplateColorsAreLabelsArticle({ onSelect }: { onSelect: (id: string) => void }) {
  const tokenList = COLOR_TOKENS.map((token) => COLOR_TOKEN_COPY[token]).join(", ");

  return (
    <HelpArticleFrame
      section="Schedule"
      title="Template colors are labels"
      meta="Updated Aug 8, 2026 · applies to Console · Schedule templates · ADR-007"
      related={
        <HelpRelated
          onSelect={onSelect}
          items={[
            { id: "drafts-then-publish", title: "Drafts then Publish in Builder" },
            { id: "open-shifts-claiming", title: "Open shifts & claiming" },
          ]}
        />
      }
    >
      <p className="help-article__body">
        Every shift template can carry a <strong>color label</strong> from a closed set of twelve
        tokens. The color is for the desk - a visual key so Night, Overtime, and Event sit apart on
        the coverage board. It is <strong>never</strong> a duty status, incident severity, SOS
        signal, or proof that something is live.
      </p>

      <h2 className="help-article__h2">The twelve labels</h2>
      <p className="help-article__body">
        Pick only from the frozen palette: <strong>{tokenList}</strong>. The product stores a token
        name (for example <strong>slate</strong> or <strong>blue</strong>), not a free-form hex from
        the browser. Unknown values fall back to slate so a bad string never paints the board.
      </p>
      <ul className="help-article__list">
        <li>
          Color lives on the <strong>template</strong>, not on each individual shift slot.
        </li>
        <li>
          On the board, template color shows as a keyline and a small chip beside the template name
          - not as a full row fill that could collide with status paints.
        </li>
        <li>
          Color never carries meaning alone: the template name stays visible next to every chip
          (accessibility, print, color vision).
        </li>
      </ul>

      <div className="help-callout" role="note">
        <p>
          <strong>Proof green is reserved.</strong> System green means something verified or live  - 
          a published schedule step, a confirmed scan, a ready state. A template you painted{" "}
          <strong>green</strong> is only a label for that shift type. It is not &quot;all clear,&quot;
          not SOS-safe, and not proof that the assignment is correct.
        </p>
      </div>

      <h2 className="help-article__h2">What officers should not infer</h2>
      <p className="help-article__body">
        Officers on the phone do not treat desk template colors as operational signals. Green on a
        template is not OK; red is not an alarm; amber is not a partial check-in. Duty, incident, and
        SOS language keep their own status styling. If you need to teach a shift type, use the
        template <strong>name</strong> and notes - not a color code only you remember.
      </p>

      <h2 className="help-article__h2">Open-fill mode is separate from color</h2>
      <p className="help-article__body">
        How an open seat gets filled is its own template setting, not a side effect of the color
        chip:
      </p>
      <ul className="help-article__list">
        <li>
          <strong>{OPEN_FILL_MODE_COPY.instant_claim}.</strong> {OPEN_FILL_MODE_HELP.instant_claim}
        </li>
        <li>
          <strong>{OPEN_FILL_MODE_COPY.request_approval}.</strong>{" "}
          {OPEN_FILL_MODE_HELP.request_approval}
        </li>
      </ul>
      <p className="help-article__body">
        You can run a blue template with instant claim and a teal template with request approval  - 
        color does not choose the fill path, and the fill path does not recolor the board.
      </p>

      <div className="help-flow" role="img" aria-label="Template color is a label; proof green is separate">
        <span className="help-flow__step">
          <span className="help-flow__name">Template color</span>
          <span className="help-flow__sub">label only · closed tokens</span>
        </span>
        <span className="help-flow__arrow" aria-hidden="true">
          ≠
        </span>
        <span className="help-flow__step help-flow__step--live">
          <span className="help-flow__name">Proof green</span>
          <span className="help-flow__sub">verified / live only</span>
        </span>
      </div>
      <p className="help-flow__cap">
        Same hue family can appear in both places - different role, different meaning.
      </p>
    </HelpArticleFrame>
  );
}

/**
 * Routes and checkpoints: Setup chain vs Patrols readiness.
 * Labels match Setup (Routes, Checkpoints, Property map) and Patrols rollout.
 */
function RoutesAndCheckpointsArticle({ onSelect }: { onSelect: (id: string) => void }) {
  return (
    <HelpArticleFrame
      section="Patrols"
      title="Routes & checkpoints"
      meta="Updated Aug 8, 2026 · applies to Console · Setup and Patrols"
      related={
        <HelpRelated
          onSelect={onSelect}
          items={[
            { id: "console-basics", title: "Console basics" },
            { id: "drafts-then-publish", title: "Drafts then Publish in Builder" },
          ]}
        />
      }
    >
      <p className="help-article__body">
        A <strong>route</strong> is a named patrol loop at one site (for example AM shift or PM
        shift). A <strong>checkpoint</strong> is a scan stop on that loop: a place the officer is
        expected to prove they visited. Sites sit under clients; routes and schedules hang off the
        site, not the client marketing name.
      </p>

      <h2 className="help-article__h2">Desk places pins; officers scan in the field</h2>
      <ul className="help-article__list">
        <li>
          Supervisors build the chain in <strong>Setup</strong>: clients and sites, then routes,
          then checkpoints. Day-to-day map placement uses <strong>Property map</strong> (draw the
          site footprint and click to place stops on satellite imagery when you have schedule
          manage).
        </li>
        <li>
          Each checkpoint can be set for <strong>QR/NFC</strong> posted tags,{" "}
          <strong>GPS geofence</strong> for drive-by vehicle patrols, or <strong>manual</strong>{" "}
          when tags are impractical.
        </li>
        <li>
          Officers prove visits on the Guard products by scanning or completing the stop the desk
          defined. This Help does not invent a full mobile Setup walk as finished product; field
          placement of new pins stays a separate, often flag-gated path.
        </li>
      </ul>

      <div className="help-callout" role="note">
        <p>
          <strong>Token minting is limited.</strong> On QR/NFC checkpoints, kits mint from the
          checkpoint row in <strong>Setup</strong> (Mint QR for QR-preferred rows, Mint kit for
          NFC-preferred rows, or Replace material).{" "}
          <strong>Patrols</strong> still has bulk Mint kit and readiness recovery. Both need{" "}
          <strong>tokens mint</strong>. Minting asks for an explicit confirmation. The one-time kit
          can be printed or exported as QR labels and/or written to NFC tags with an external
          writer; the console does not write blank NFC tags in the browser. This article does not
          walk secret keys or device provisioning.
        </p>
      </div>

      <h2 className="help-article__h2">Patrols vs Setup</h2>
      <p className="help-article__body">
        <strong>Setup</strong> is the onboarding workbench that creates and edits the property
        chain, including one-checkpoint tag kits for QR/NFC rows. <strong>Patrols</strong> is
        rollout readiness: whether routes and checkpoints are ready, material verification that the
        latest checkpoint material was actually scanned after creation, and bulk route token
        minting when allowed. Live coverage during a shift is a different desk view (Command Center
        / coverage), not the Patrols rollout page itself.
      </p>
      <ul className="help-article__list">
        <li>
          Setup nav needs route rollout manage. Editing clients, routes, and checkpoints needs the
          manage capabilities the page already gates (commonly schedule manage for the map and
          editors).
        </li>
        <li>
          Optional site boundaries and asset polygons (buildings, parking) import as GeoJSON and
          show as layers on the Command Center map under or around checkpoint pins.
        </li>
      </ul>

      <h2 className="help-article__h2">What supervisors see on maps</h2>
      <p className="help-article__body">
        On Command Center, desk maps can show the property line, asset outlines, and checkpoint
        pins for the active workspace when those layers are configured. Property map under Setup is
        the place to edit footprints and place stops. Pins without a route yet can still exist as
        walk drafts or promoted pins; attach them to a route when the patrol loop is ready.
      </p>

      <h2 className="help-article__h2">Presence without a pin</h2>
      <p className="help-article__body">
        Officers can record free-form or ad-hoc presence when product flags allow it. Those events
        are honest &quot;Here (no pin)&quot; style presence when no checkpoint name applies. They
        are not a substitute for a planned checkpoint scan, and they do not invent a desk pin that
        was never placed.
      </p>

      <div
        className="help-flow"
        role="img"
        aria-label="Flow: site, route, checkpoints, field scan"
      >
        <span className="help-flow__step">
          <span className="help-flow__name">Site</span>
          <span className="help-flow__sub">under a client</span>
        </span>
        <span className="help-flow__arrow" aria-hidden="true">
          →
        </span>
        <span className="help-flow__step">
          <span className="help-flow__name">Route</span>
          <span className="help-flow__sub">named patrol loop</span>
        </span>
        <span className="help-flow__arrow" aria-hidden="true">
          →
        </span>
        <span className="help-flow__step help-flow__step--live">
          <span className="help-flow__name">Checkpoints</span>
          <span className="help-flow__sub">scan stops · field proof</span>
        </span>
      </div>
      <p className="help-flow__cap">
        Desk builds the loop in Setup; officers complete stops in the field; Patrols tracks
        readiness and material proof.
      </p>
    </HelpArticleFrame>
  );
}

/**
 * Admin Members invite: reserve membership + Auth sign-in email.
 * Labels match MemberInvitePanel (Send invite / Resend sign-in email / Revoke).
 */
function InvitingYourTeamArticle({ onSelect }: { onSelect: (id: string) => void }) {
  return (
    <HelpArticleFrame
      section="Admin"
      title="Inviting your team"
      meta="Updated Aug 9, 2026 · applies to Console · Admin · Members"
      related={
        <HelpRelated
          onSelect={onSelect}
          items={[
            { id: "console-basics", title: "Console basics" },
            { id: "alert-channel-policies", title: "Alert channel policies" },
            { id: "roles-permissions", title: "Roles & permissions" },
          ]}
        />
      }
    >
      <p className="help-article__body">
        Inviting someone reserves a seat on this account and tries to put them in Supabase Auth so
        they can sign in. You do that under <strong>Admin</strong>, then <strong>Members</strong>,
        in the invite form. You need <strong>member manage</strong> to send, resend, or revoke.
        Without it you may still see pending invites read-only.
      </p>

      <h2 className="help-article__h2">Send invite</h2>
      <p className="help-article__body">
        Fill the form labeled <strong>Invite a member</strong>, then press{" "}
        <strong>Send invite</strong>. The product records:
      </p>
      <ul className="help-article__list">
        <li>
          <strong>Email</strong> (required) - the address they must use to join.
        </li>
        <li>
          <strong>Role</strong> - one of <strong>Guard</strong>, <strong>Supervisor</strong>, or{" "}
          <strong>Admin</strong>. Support is not invitable from this form.
        </li>
        <li>
          <strong>Display name</strong> - required for guard invites; optional for supervisor and
          admin.
        </li>
        <li>
          <strong>Phone</strong> - optional contact on the invite record. This is not an SMS invite
          path.
        </li>
      </ul>
      <p className="help-article__body">
        On success the desk reserves membership for that email and requests a sign-in invite email
        so the person appears under Authentication users. If that email already has a sign-in
        account, membership is still reserved and they can use Login code or password instead of a
        new invite link.
      </p>

      <div className="help-callout" role="note">
        <p>
          <strong>Invite is not the same as Resend.</strong> Send invite creates (or reuses) the
          pending membership reservation. <strong>Resend sign-in email</strong> only retries the
          Auth email for an existing pending row when the first mail never landed or was lost.
        </p>
      </div>

      <h2 className="help-article__h2">Pending list</h2>
      <ul className="help-article__list">
        <li>
          Pending rows show email (and display name when present), role, status, and expiry when
          set.
        </li>
        <li>
          <strong>Resend sign-in email</strong> is for pending invites that still need Auth mail.
        </li>
        <li>
          <strong>Revoke</strong> cancels a pending invite after confirm. They can be re-invited
          later. Anyone who already accepted is unaffected.
        </li>
        <li>
          Common refusals include: not enough permission, invalid email, unsupported role, pending
          invite already exists for this email, active membership already exists, pending invite not
          found, or pending invite expired. The form shows those product sentences when the server
          returns them.
        </li>
      </ul>

      <h2 className="help-article__h2">What the invitee does</h2>
      <p className="help-article__body">
        They receive the Auth sign-in invite (when mail succeeds) and sign in with the invited
        address: a password set from that email, or a login code. Desk people finish joining in
        the Console. Officers can also sign in to a Guard field product with the same email, where
        a pending invite for their address can be accepted in the app. This article still does not
        invent a universal-link invite path; the email address is the join key either way.
      </p>

      <h2 className="help-article__h2">Roster after join</h2>
      <p className="help-article__body">
        Accepted people show in the Members directory below the invite panel. From there, managers
        can edit names and phones, change roles, and suspend, restore, or remove members when they
        have member manage. Removed members can return when re-invited. The full role matrix is in
        Roles &amp; permissions.
      </p>

      <div
        className="help-flow"
        role="img"
        aria-label="Flow: send invite, Auth email, join, roster"
      >
        <span className="help-flow__step">
          <span className="help-flow__name">Send invite</span>
          <span className="help-flow__sub">reserve membership</span>
        </span>
        <span className="help-flow__arrow" aria-hidden="true">
          →
        </span>
        <span className="help-flow__step">
          <span className="help-flow__name">Sign-in email</span>
          <span className="help-flow__sub">or Resend if needed</span>
        </span>
        <span className="help-flow__arrow" aria-hidden="true">
          →
        </span>
        <span className="help-flow__step help-flow__step--live">
          <span className="help-flow__name">Joined</span>
          <span className="help-flow__sub">on Members roster</span>
        </span>
      </div>
      <p className="help-flow__cap">
        Pending means reserved but not yet accepted. Revoke only affects pending rows.
      </p>
    </HelpArticleFrame>
  );
}

/**
 * Admin alert floor: which channels each event kind fires on for the account.
 * Language matches Admin "Alerts & channel policies" (not chat).
 */
function AlertChannelPoliciesArticle({ onSelect }: { onSelect: (id: string) => void }) {
  return (
    <HelpArticleFrame
      section="Admin"
      title="Alert channel policies"
      meta="Updated Aug 8, 2026 · applies to Console · Admin · Alerts & channel policies"
      related={
        <HelpRelated
          onSelect={onSelect}
          items={[
            { id: "roles-permissions", title: "Roles & permissions" },
            { id: "inviting-team", title: "Inviting your team" },
          ]}
        />
      }
    >
      <p className="help-article__body">
        An <strong>alert channel policy</strong> is the account floor for delivery: for each alert
        event kind, which channels fire. The desk sets that matrix once for the company. Members may
        add notifications for themselves later. They never subtract below the account floor.
      </p>

      <h2 className="help-article__h2">Where you configure it</h2>
      <p className="help-article__body">
        Open <strong>Admin</strong>, then <strong>Alerts &amp; channel policies</strong>. You need
        policy view to see the matrix and policy manage to change it. Rows are event kinds (for
        example late clock-in, checkpoint missed, or duress or SOS). Columns are channels:{" "}
        <strong>In-app</strong>, <strong>Push</strong>, <strong>Email</strong>, and{" "}
        <strong>SMS</strong>.
      </p>
      <ul className="help-article__list">
        <li>
          This is delivery fan-out for product alert events. It is not free-form team chat and not
          a Discord-style channel list.
        </li>
        <li>
          The matrix comes from the server. If policies are not available on this server yet, Admin
          says so and shows no fake switches.
        </li>
        <li>
          SMS may appear as a column so the layout stays stable. SMS has no delivery path yet, so
          it stays off and is not saved as on.
        </li>
      </ul>

      <div className="help-callout" role="note">
        <p>
          <strong>Safety events stay on.</strong> Duress or SOS cannot be switched off for In-app
          and Push. Quiet hours (optional account quiet window) never hold those safety alerts. The
          server refuses invalid safety floor changes even if the desk UI were wrong.
        </p>
      </div>

      <h2 className="help-article__h2">What the desk vs the field sees</h2>
      <p className="help-article__body">
        Supervisors and admins configure the floor in Console Admin. Officers and other members
        receive alerts on the channels the floor allows for each event kind. This article does not
        invent mobile SOS response screens: safety delivery is the floor rule above; response and
        incident tooling live elsewhere when those Help topics ship.
      </p>

      <h2 className="help-article__h2">What a member can add for themselves</h2>
      <p className="help-article__body">
        Under <strong>Admin</strong>, then <strong>My alert channels</strong>, any signed-in member
        of the account sets their own preferences. That page needs no policy capability, because it
        only ever writes the caller&apos;s own row. It is <strong>additive</strong>: switching a
        channel on there sends the event to that person as well; switching it off again removes only
        their own addition and never drops them below this floor. SMS is unavailable there too, and
        personal email additions are refused for duress and SOS.
      </p>

      <h2 className="help-article__h2">Quiet hours</h2>
      <p className="help-article__body">
        Quiet hours are optional. When enabled, they can suppress non-safety deliveries during a
        local time window. They never silence duress or SOS. Exempt event labels appear next to the
        quiet-hours control so the desk can see what still breaks through.
      </p>

      <div
        className="help-flow"
        role="img"
        aria-label="Flow: event kind, channel matrix, delivery floor"
      >
        <span className="help-flow__step">
          <span className="help-flow__name">Event kind</span>
          <span className="help-flow__sub">missed checkpoint, SOS, …</span>
        </span>
        <span className="help-flow__arrow" aria-hidden="true">
          →
        </span>
        <span className="help-flow__step">
          <span className="help-flow__name">Channel matrix</span>
          <span className="help-flow__sub">In-app · Push · Email · SMS</span>
        </span>
        <span className="help-flow__arrow" aria-hidden="true">
          →
        </span>
        <span className="help-flow__step help-flow__step--live">
          <span className="help-flow__name">Account floor</span>
          <span className="help-flow__sub">members cannot drop below</span>
        </span>
      </div>
      <p className="help-flow__cap">
        Save only after you review the matrix. Unsaved flips are not live delivery rules.
      </p>
    </HelpArticleFrame>
  );
}

/**
 * Founder-only phone env switch (PRD-001AC). Superuser Help only; never teach this to guards.
 * Labels and host form match apps/mobile host unlock allowlist.
 */
function PhoneStagingBetaArticle({ onSelect }: { onSelect: (id: string) => void }) {
  return (
    <HelpArticleFrame
      section="Platform"
      title="Phone: staging and beta vs production"
      meta="Updated Aug 9, 2026 · founder device only · PRD-001AC"
      related={
        <HelpRelated
          onSelect={onSelect}
          items={[
            { id: "guard-app-access-queue", title: "Guard app access: working the queue" },
            { id: "console-basics", title: "Console basics" },
          ]}
        />
      }
    >
      <p className="help-article__body">
        TestFlight, Google Play internal testing, and production phone builds always{" "}
        <strong>boot into production</strong> Supabase (same backend as https://console.thesecops.app).
        Founders can point a personal phone at staging for dogfood without baking staging keys into
        every build. This is <strong>not</strong> for officers or client pilots.
      </p>

      <p className="help-article__body">
        When a company admin requests access for an officer (Admin, then Members, Request Guard
        app access), the request lands on <strong>Platform</strong>, tab{" "}
        <strong>Access queue</strong>. Work it there with the steps below as your store-side
        runbook; the queue article covers the marks and statuses.
      </p>

      <h2 className="help-article__h2">Install the officer app (founders)</h2>
      <ul className="help-article__list">
        <li>
          <strong>iOS:</strong> Apple TestFlight invite for SecOps Guard (App Store Connect
          distribution).
        </li>
        <li>
          <strong>Android:</strong> Google Play <strong>internal testing</strong> only. Sign into
          Play with a Google account that is already on your Internal testing email list, open the
          opt-in link, become a tester, then install from Play. The link does not auto-enroll
          strangers; add each Google account email in Play Console first.
        </li>
      </ul>
      <p className="help-article__body">
        Android internal testing opt-in:{" "}
        <a
          href="https://play.google.com/apps/internaltest/4701044665004918804"
          target="_blank"
          rel="noreferrer"
        >
          play.google.com/apps/internaltest/…
        </a>
      </p>
      <div className="help-callout" role="note">
        <p>
          Package <code>com.nickranks.secops.guard</code>. Up to 100 listed Google accounts.
          Not a public store listing for officers. Closed/production tracks get their own install
          path later; keep this link on the founder tester list only.
        </p>
      </div>

      <div className="help-callout" role="note">
        <p>
          <strong>Exact Environment labels (type only these words):</strong>{" "}
          <code>staging</code> or <code>beta</code>. Lowercase, no spaces, no URL. Anything else is
          rejected.
        </p>
      </div>

      <h2 className="help-article__h2">Switch the phone to staging</h2>
      <ol className="help-article__list">
        <li>
          Open the field app on your device. Use a recent TestFlight or Play internal build that
          includes founder host unlock (not every old build).
        </li>
        <li>
          Find the <strong>version / build</strong> line (same place that shows the app version).
          Tap it <strong>seven times</strong> in a row until the <strong>Environment</strong> sheet
          opens.
        </li>
        <li>
          In <strong>Environment label</strong>, type exactly: <code>staging</code>
        </li>
        <li>
          Tap <strong>Verify and save</strong>. Wait for success. If verify fails, stay on
          production and check network; do not invent other labels.
        </li>
        <li>
          <strong>Fully quit and restart</strong> the app (swipe away from the app switcher, then
          open again). The override applies on the next cold start.
        </li>
        <li>
          Confirm a persistent <strong>STAGING</strong> (or <strong>BETA</strong>) pill in the
          shell. If you still see production only, the restart did not complete or the label was
          not saved.
        </li>
      </ol>

      <h2 className="help-article__h2">What each label means</h2>
      <ul className="help-article__list">
        <li>
          <code>staging</code> → staging Supabase. Pair the phone with{" "}
          <strong>https://beta.console.thesecops.app</strong> (not the production console).
        </li>
        <li>
          <code>beta</code> → same staging Supabase today (alias). Still use beta.console for the
          desk. Future third host may split; for now treat it like staging.
        </li>
        <li>
          <strong>Use production</strong> on the same sheet → clear override; full restart; phone
          matches https://console.thesecops.app again.
        </li>
      </ul>

      <h2 className="help-article__h2">Safe dogfood pairing</h2>
      <ul className="help-article__list">
        <li>
          Phone <strong>STAGING</strong> + console <strong>beta.console</strong> = test stack. Ship
          features here first with <code>npm run deploy:console:staging</code> and staging DB
          migrations.
        </li>
        <li>
          Phone <strong>production</strong> + console <strong>console.thesecops.app</strong> = real
          client data (for example Stryke Team Elite or a client workspace).
        </li>
        <li>
          Do not mix: staging phone against production console (or the reverse) looks empty or
          wrong.
        </li>
      </ul>

      <h2 className="help-article__h2">Back to production for a client walk</h2>
      <ol className="help-article__list">
        <li>
          Seven taps again → <strong>Use production</strong>.
        </li>
        <li>Fully quit and restart the app.</li>
        <li>
          Confirm the status line is production (no STAGING pill) before you demo for a client.
        </li>
      </ol>

      <div className="help-callout" role="note">
        <p>
          Superusers only see this Help article. Do not paste these steps into public support,
          App Store review notes, or officer training.
        </p>
      </div>
    </HelpArticleFrame>
  );
}

/**
 * Roles and gates, from the real matrix (account_role_permissions migrations). Every claim
 * here names a shipped permission key or a shipped refusal sentence; nothing is aspirational.
 */
function RolesAndPermissionsArticle({ onSelect }: { onSelect: (id: string) => void }) {
  return (
    <HelpArticleFrame
      section="Getting around"
      title="Roles & permissions"
      meta="Updated Aug 9, 2026 · applies to Console · roles and permission gates"
      related={
        <HelpRelated
          onSelect={onSelect}
          items={[
            { id: "inviting-team", title: "Inviting your team" },
            { id: "console-basics", title: "Console basics" },
            { id: "alert-channel-policies", title: "Alert channel policies" },
          ]}
        />
      }
    >
      <p className="help-article__body">
        Access has two layers. A person holds one <strong>membership role</strong> on the account
        (Guard, Supervisor, Admin, or Support), and that role grants a fixed set of{" "}
        <strong>permission keys</strong>. The console hides controls you cannot use, but hiding is
        a courtesy: the server checks the same keys on every read and write and refuses on its own.
        A hidden button is never the security boundary.
      </p>

      <h2 className="help-article__h2">The four roles</h2>
      <ul className="help-article__list">
        <li>
          <strong>Guard</strong> is the field seat: capture checkpoint scans, punch their own
          timeclock, see and respond to their own published shifts. In the console a guard sees
          almost nothing management-shaped, because reads are scoped to their own data server-side.
        </li>
        <li>
          <strong>Supervisor</strong> runs the desk: view and manage the schedule, review scans,
          view and manage alerts, work incidents and evidence, view reports and client profiles,
          manage the team timeclock, mint checkpoint token kits, post to Feed, and manage Channels
          rooms. Supervisors read policy but do not change it.
        </li>
        <li>
          <strong>Admin</strong> is supervisor plus account management: invite and manage members,
          change policy, open Setup (route rollout manage), manage checkpoint tokens beyond
          minting, and hold the billing and brand keys.
        </li>
        <li>
          <strong>Support</strong> is a read-mostly seat for helping an account: it can view
          scans, alerts, evidence, reports, schedules, policy, and client profiles, without the
          manage writes. Support is not invitable from the console invite form.
        </li>
      </ul>

      <h2 className="help-article__h2">What gates the navigation</h2>
      <ul className="help-article__list">
        <li>
          <strong>Setup</strong> appears only with <strong>route rollout manage</strong>, which is
          admin-only. Supervisors work routes from Patrols and the schedule, not Setup.
        </li>
        <li>
          <strong>Admin</strong> appears only with <strong>member manage</strong>, also admin-only.
        </li>
        <li>
          <strong>Platform</strong> and <strong>Help</strong> are platform superadmin surfaces.
          Platform superadmin is not a membership role at all: it is a separate platform grant that
          never shows on the account roster.
        </li>
        <li>
          Every other item (Command Center, Patrols, Schedule, Feed, Channels, Incidents, Reports)
          is visible to any signed-in member. Visibility is not access: the data inside each page
          still answers to the caller&apos;s role and keys.
        </li>
      </ul>

      <h2 className="help-article__h2">Inside Admin</h2>
      <ul className="help-article__list">
        <li>
          Policy sections need <strong>policy view</strong> to see and{" "}
          <strong>policy manage</strong> to change (site policies, duty tracking, mobile flags,
          alert channel policies, consent).
        </li>
        <li>
          Member lifecycle (invite, edit, suspend, restore, remove) needs{" "}
          <strong>member manage</strong>. Device revoke and restore follow{" "}
          <strong>schedule manage</strong>.
        </li>
        <li>
          <strong>My alert channels</strong> needs no capability at all: every member owns their
          own additive notification preferences, and the server binds the row to the caller.
        </li>
        <li>
          Commercial readiness reads the admin-only <strong>billing manage</strong> and{" "}
          <strong>brand manage</strong> keys.
        </li>
      </ul>

      <div className="help-callout" role="note">
        <p>
          <strong>Two refusals you will actually meet:</strong> &quot;You cannot change your own
          role.&quot; and &quot;At least one active admin is required for this account.&quot; Both
          come from the server, word for word, when a role edit would strand the account or
          promote yourself. Plan admin coverage before demoting anyone.
        </p>
      </div>

      <h2 className="help-article__h2">Superadmin and support sessions</h2>
      <p className="help-article__body">
        A platform superadmin can open a time-limited <strong>support session</strong> from a
        member row (Support as…). For that session the console acts as the target membership and
        the superadmin&apos;s platform elevation is suppressed, so they see exactly what the member
        sees. Everything done in the session is recorded against the superadmin&apos;s own
        account. The session banner cannot be dismissed while it is active.
      </p>

      <div className="help-flow" role="img" aria-label="Flow: role, permission keys, server check">
        <span className="help-flow__step">
          <span className="help-flow__name">Role</span>
          <span className="help-flow__sub">guard · supervisor · admin · support</span>
        </span>
        <span className="help-flow__arrow" aria-hidden="true">
          →
        </span>
        <span className="help-flow__step">
          <span className="help-flow__name">Permission keys</span>
          <span className="help-flow__sub">fixed per role</span>
        </span>
        <span className="help-flow__arrow" aria-hidden="true">
          →
        </span>
        <span className="help-flow__step help-flow__step--live">
          <span className="help-flow__name">Server check</span>
          <span className="help-flow__sub">refuses on its own</span>
        </span>
      </div>
      <p className="help-flow__cap">
        The console hides what you cannot use; the server refuses regardless. Teach the role, not
        the button.
      </p>
    </HelpArticleFrame>
  );
}

/**
 * SOS desk to incident record, with the honest gap stated: no product join exists between the
 * SOS log and a field incident. Labels match SosResponseDesk and IncidentDetail exactly.
 */
function SosToIncidentArticle({ onSelect }: { onSelect: (id: string) => void }) {
  return (
    <HelpArticleFrame
      section="Incidents"
      title="From SOS to incident report"
      meta="Updated Aug 9, 2026 · applies to Console · Command Center SOS desk and Incidents"
      related={
        <HelpRelated
          onSelect={onSelect}
          items={[
            { id: "alert-channel-policies", title: "Alert channel policies" },
            { id: "console-basics", title: "Console basics" },
          ]}
        />
      }
    >
      <p className="help-article__body">
        An <strong>SOS</strong> is a life-safety signal from an officer&apos;s phone. It lands on
        the <strong>Command Center</strong> desk in the <strong>SOS response</strong> section, with
        a ribbon that reads <strong>Active SOS</strong> while anything is open. An{" "}
        <strong>incident report</strong> is a different thing: a field record with photos and
        review states on the <strong>Incidents</strong> page. Work the SOS first. The paper record
        comes second, and this article shows the honest bridge between the two.
      </p>

      <h2 className="help-article__h2">Work the SOS</h2>
      <ol className="help-article__list">
        <li>
          Open <strong>Command Center</strong>. Active signals sit in the SOS response section;
          admins, supervisors, and support seats can work them.
        </li>
        <li>
          Press <strong>Join response</strong>. Joining says &quot;the desk is on this&quot;
          without resolving anything, and more than one responder can join the same signal. Every
          join lands on the SOS log.
        </li>
        <li>
          Use the duty last-known position when the desk can stand behind one. Coordinates show
          four decimals (about eleven meters): a place, not a target. When duty tracking is off,
          the officer is off duty, or the last fix is too old, the desk says the position is
          unavailable instead of guessing.
        </li>
        <li>
          Call the officer and follow your own escalation runbook. The product records; it does
          not dispatch. There is no send-nearest-help button today, and this article will not
          pretend there is.
        </li>
      </ol>

      <h2 className="help-article__h2">Stand down with the true outcome</h2>
      <p className="help-article__body">
        Press <strong>Stand down…</strong>, choose what actually happened, then{" "}
        <strong>Stand down and log</strong>. The desk refuses to stand down without an outcome:
        &quot;Choose what happened before standing down. It goes on the record.&quot;
      </p>
      <ul className="help-article__list">
        <li>
          The outcomes are fixed: <strong>Accidental press</strong>,{" "}
          <strong>Officer safe, resolved</strong>, <strong>False alarm</strong>,{" "}
          <strong>Drill</strong>, and <strong>Escalated to emergency services</strong>.
        </li>
        <li>An optional note takes up to 280 characters. The outcome stays a structured column.</li>
        <li>
          Standing down clears the signal; it deletes nothing. The press, every join, and the
          stand-down stay on the SOS log, and the signal remains visible under{" "}
          <strong>Recently cleared</strong>.
        </li>
      </ul>

      <div className="help-callout" role="note">
        <p>
          <strong>Delivery cannot be silenced.</strong> SOS and duress always reach management
          in-app and by push, whatever the alert channel matrix or quiet hours say. See Alert
          channel policies for the floor rules.
        </p>
      </div>

      <h2 className="help-article__h2">File the incident record</h2>
      <ul className="help-article__list">
        <li>
          Formal incident reports are <strong>field records</strong>: the officer files them from
          the Guard products, with photos attached as evidence. The console reads them on the{" "}
          <strong>Incidents</strong> page; guards see zero incident rows in the console.
        </li>
        <li>
          Desk review is two moves with <strong>evidence manage</strong>:{" "}
          <strong>Start review</strong> moves a submitted record to under review, and{" "}
          <strong>Lock the record</strong> files it permanently. Lock is terminal and the desk
          confirms before it happens: a locked record cannot be changed.
        </li>
        <li>
          Photos open through short-lived signed links on an explicit click. Nothing downloads by
          itself, and links expire in minutes.
        </li>
      </ul>

      <div className="help-callout" role="note">
        <p>
          <strong>The honest gap:</strong> there is no button that turns an SOS into an incident,
          and no shared reference number joins the two records today. If an SOS deserves a formal
          report: stand down with the true outcome and a note, have the officer file an incident
          report from the field, then review and lock it on Incidents. Match them by clock time and
          note text. Do not promise clients a joined SOS-to-incident record yet.
        </p>
      </div>

      <div
        className="help-flow"
        role="img"
        aria-label="Flow: SOS press, join and respond, stand down, field incident, lock"
      >
        <span className="help-flow__step">
          <span className="help-flow__name">SOS press</span>
          <span className="help-flow__sub">Active SOS on the desk</span>
        </span>
        <span className="help-flow__arrow" aria-hidden="true">
          →
        </span>
        <span className="help-flow__step">
          <span className="help-flow__name">Join, then stand down</span>
          <span className="help-flow__sub">outcome on the record</span>
        </span>
        <span className="help-flow__arrow" aria-hidden="true">
          →
        </span>
        <span className="help-flow__step help-flow__step--live">
          <span className="help-flow__name">Incident filed</span>
          <span className="help-flow__sub">reviewed · locked</span>
        </span>
      </div>
      <p className="help-flow__cap">
        The SOS log records the response. The incident record files the story. Today you connect
        them by discipline, not by a link.
      </p>
    </HelpArticleFrame>
  );
}

/**
 * Founder queue discipline for Guard app access requests. Mirrors the shipped founder desk
 * (Platform > Access queue) and the design lock: green is Installed only, marks are personal
 * claims, and the Play opt-in URL lives in the phone article, nowhere else.
 */
function GuardAppAccessQueueArticle({ onSelect }: { onSelect: (id: string) => void }) {
  return (
    <HelpArticleFrame
      section="Platform"
      title="Guard app access: working the queue"
      meta="Updated Aug 9, 2026 · founder desk · Platform · Access queue"
      related={
        <HelpRelated
          onSelect={onSelect}
          items={[
            { id: "phone-staging-beta", title: "Phone: staging and beta vs production" },
            { id: "console-basics", title: "Console basics" },
          ]}
        />
      }
    >
      <p className="help-article__body">
        The Guard app is invite only, and a work login is not a store account. Company admins send
        requests from <strong>Admin</strong>, then <strong>Members</strong> (Request Guard app
        access), with the Apple ID or Google account signed in on the officer&apos;s phone. Those
        requests land on <strong>Platform</strong>, tab <strong>Access queue</strong>. You add the
        address by hand in App Store Connect or Play Console, then record it. SecOps does not talk
        to the stores, and the copy never pretends it does.
      </p>

      <h2 className="help-article__h2">Work the queue</h2>
      <ol className="help-article__list">
        <li>
          Start on the <strong>Waiting on me</strong> filter. It counts only Requested and Part
          added, the rows with you at the end of them; the tab badge shows the same number and
          disappears at zero.
        </li>
        <li>
          Press <strong>Copy</strong> on the store address. Copy is the real first move: paste it
          into the store console by hand.
        </li>
        <li>
          Come back and mark it: <strong>Mark added</strong> on a single-platform row, or{" "}
          <strong>Mark iOS</strong> / <strong>Mark Android</strong> when a Both request is half
          done. Marking is a personal claim that you did the work in the other console, which is
          why there is no bulk mark and never will be one on this desk.
        </li>
        <li>
          Open a row for the note from the admin, the log, and per-store marks when a Both request
          needs both.
        </li>
      </ol>

      <h2 className="help-article__h2">Read the statuses honestly</h2>
      <ul className="help-article__list">
        <li>
          <strong>Requested</strong> and <strong>Part added</strong> are waiting on you.
        </li>
        <li>
          <strong>Added</strong> means every requested store has the address; Apple or Google now
          emails the officer directly. Waiting on them, not on you.
        </li>
        <li>
          <strong>Installed</strong> is the only green, and it is derived: a trusted phone checked
          in running Guard after your mark. You cannot set it by hand, and that is the point.
        </li>
        <li>
          An Added row with no install after a few days gets an amber sub-line. The delay belongs
          to the store and the officer: tell the admin to have them check spam.
        </li>
      </ul>

      <h2 className="help-article__h2">Close with a reason</h2>
      <p className="help-article__body">
        Closing needs one of four reasons: <strong>Duplicate</strong>,{" "}
        <strong>Left the company</strong>, <strong>Wrong address</strong>, or{" "}
        <strong>Declined</strong>. The admin sees the reason on their member row.{" "}
        <strong>Wrong address</strong> is the correction path once you have marked a store: it
        hands the request back so the admin resubmits with the fixed address. Closed rows stay on
        the record; nothing in this queue is deleted.
      </p>

      <div className="help-callout" role="note">
        <p>
          <strong>Keep the opt-in link off tenant surfaces.</strong> The install runbook itself
          (TestFlight steps, the Play internal testing opt-in link, the tester list) lives in
          Phone: staging and beta vs production and nowhere else in the product. An admin who
          pastes that link has just made an invite-only app self-serve.
        </p>
      </div>

      <p className="help-article__body">
        What the admin sees back: their member row reads Requested, then Waiting on Apple invite or
        Waiting on Google invite, then Installed once the phone checks in, or Closed with your
        reason. Softer words, same record.
      </p>

      <div
        className="help-flow"
        role="img"
        aria-label="Flow: admin request, copy and add by hand, mark, installed"
      >
        <span className="help-flow__step">
          <span className="help-flow__name">Admin request</span>
          <span className="help-flow__sub">store account · not the login</span>
        </span>
        <span className="help-flow__arrow" aria-hidden="true">
          →
        </span>
        <span className="help-flow__step">
          <span className="help-flow__name">Copy · add by hand · mark</span>
          <span className="help-flow__sub">a recorded claim</span>
        </span>
        <span className="help-flow__arrow" aria-hidden="true">
          →
        </span>
        <span className="help-flow__step help-flow__step--live">
          <span className="help-flow__name">Installed</span>
          <span className="help-flow__sub">the phone reported it</span>
        </span>
      </div>
      <p className="help-flow__cap">
        Green means nothing left to do, and only the phone can say so.
      </p>
    </HelpArticleFrame>
  );
}

function ShareIdeaFooter() {
  const openFeedback = useContext(FeedbackLaunchContext);
  return (
    <div className="help-foot">
      <p>Spot something wrong or missing in this article?</p>
      <div className="help-foot__actions">
        <button
          type="button"
          className="help-foot__action"
          onClick={(event) => openFeedback("problem", event.currentTarget)}
        >
          Report a problem
        </button>
        <button
          type="button"
          className="help-foot__action"
          onClick={(event) => openFeedback("idea", event.currentTarget)}
        >
          Share an idea
        </button>
      </div>
    </div>
  );
}
